import {
  buildHubupGroupInviteUrl,
  fetchHubupGroup,
  fetchHubupGroupMessages,
  fetchHubupGroupTopics,
  fetchMyHubupCommunities,
  resolveHubupAssetUrl,
} from '../../../api/hubup';
import { generateWebCreatorHubupQr } from '../../../api/webCreator';

export function isHubupEditorTopic(topic) {
  return String(topic?.topicKind || '').trim() === 'editor';
}

function isPdfPath(path) {
  return /\.pdf(\?|$)/i.test(String(path || ''));
}

function isVideoPath(path) {
  return /\.(mp4|webm|mov|m4v)(\?|$)/i.test(String(path || ''));
}

function messageImageUrls(message) {
  if (Array.isArray(message?.imageUrls) && message.imageUrls.length > 0) {
    return message.imageUrls.filter(Boolean);
  }
  if (message?.imageUrl) return [message.imageUrl];
  return [];
}

function pdfLabel(path) {
  const raw = String(path || '').split('/').pop() || 'Document.pdf';
  try {
    return decodeURIComponent(raw.replace(/\?.*$/, ''));
  } catch {
    return raw;
  }
}

export function messageToCachedNotification(message, community, feedBrand = {}) {
  const mediaItems = messageImageUrls(message).slice(0, 2).map((path, i) => {
    const src = resolveHubupAssetUrl(path);
    if (!src) return null;
    if (isPdfPath(path)) {
      return { key: `${message.id}-pdf-${i}`, kind: 'pdf', label: pdfLabel(path), src };
    }
    if (isVideoPath(path)) {
      return { key: `${message.id}-vid-${i}`, kind: 'video', src };
    }
    return { key: `${message.id}-img-${i}`, kind: 'image', src };
  }).filter(Boolean);

  return {
    id: message.id,
    title: message.title || '',
    bodyHtml: message.bodyHtml || '',
    topicTitle: message.topicTitle || '',
    linkUrl: message.linkUrl || '',
    priority: message.priority === 'high' ? 'high' : 'normal',
    hasForm: Boolean(message.formId),
    feedName: community?.name || message.topicTitle || 'HubUP',
    groupIsPro: community?.isPro === true,
    groupIconUrl: community?.groupIconUrl || '',
    groupFeedFont: feedBrand.font || feedBrand.groupFeedFont || '',
    groupColorHexBg: feedBrand.colorHexBg || feedBrand.groupColorHexBg || '',
    groupColorHexFont: feedBrand.colorHexFont || feedBrand.groupColorHexFont || '',
    mediaItems,
    createdAt: message.createdAt || null,
  };
}

export async function fetchHubupCommunitiesForPicker() {
  const res = await fetchMyHubupCommunities();
  return res?.data?.communities || [];
}

export async function fetchHubupTopicsForPicker(groupId) {
  if (!groupId) return [];
  const res = await fetchHubupGroupTopics(groupId);
  const topics = res?.data?.topics || [];
  return topics.filter((t) => !isHubupEditorTopic(t));
}

export async function fetchHubupGroupBrand(groupId) {
  if (!groupId) return {};
  try {
    const res = await fetchHubupGroup(groupId);
    const g = res?.data?.group || res?.data || {};
    return {
      font: g.font || g.feedFont || '',
      colorHexBg: g.colorHexBg || '',
      colorHexFont: g.colorHexFont || '',
    };
  } catch {
    return {};
  }
}

export async function fetchNotificationsSnapshot(groupId, topicId, count = 3, community = null) {
  if (!groupId) return { cachedNotifications: [], fetchedAt: null };

  const [messagesRes, brand] = await Promise.all([
    fetchHubupGroupMessages(groupId),
    fetchHubupGroupBrand(groupId),
  ]);

  let messages = messagesRes?.data?.messages || [];

  try {
    const topicsRes = await fetchHubupGroupTopics(groupId);
    const editorIds = new Set(
      (topicsRes?.data?.topics || [])
        .filter(isHubupEditorTopic)
        .map((t) => t.id),
    );
    if (editorIds.size > 0) {
      messages = messages.filter((m) => !editorIds.has(m.topicId));
    }
  } catch {
    /* ignore topic filter */
  }

  messages = messages.filter((m) => {
    if (!topicId) return true;
    return m.topicId === topicId;
  });

  const cachedNotifications = messages
    .slice(0, Math.max(1, Math.min(3, Number(count) || 3)))
    .map((m) => messageToCachedNotification(m, community, brand));

  return {
    cachedNotifications,
    fetchedAt: new Date().toISOString(),
    groupFeedFont: brand.font || '',
    groupColorHexBg: brand.colorHexBg || '',
    groupColorHexFont: brand.colorHexFont || '',
  };
}

export function communityPickerPatch(community, extra = {}) {
  if (!community) {
    return {
      groupId: '',
      communityName: '',
      inviteCode: '',
      groupIconUrl: '',
      isPro: false,
      ...extra,
    };
  }
  return {
    groupId: community.id,
    communityName: community.name || '',
    inviteCode: community.inviteCode || '',
    groupIconUrl: community.groupIconUrl || '',
    isPro: community.isPro === true,
    ...extra,
  };
}

export async function refreshHubupComponentProps(comp) {
  if (!comp?.props?.groupId) return comp.props;

  if (comp.type === 'hubupQr') {
    const communities = await fetchHubupCommunitiesForPicker();
    const community = communities.find((c) => c.id === comp.props.groupId);
    if (!community) return comp.props;
    const merged = {
      ...comp.props,
      ...communityPickerPatch(community),
      inviteUrl: buildHubupGroupInviteUrl(community.inviteCode),
    };
    try {
      const res = await generateWebCreatorHubupQr({
        componentId: comp.id,
        groupId: merged.groupId,
        inviteCode: merged.inviteCode,
        groupIconUrl: merged.groupIconUrl,
        inviteUrl: merged.inviteUrl,
        communityName: merged.communityName,
        qrImageUrl: merged.qrImageUrl,
        qrFingerprint: merged.qrFingerprint,
      });
      const serverProps = res?.data?.props;
      if (serverProps) {
        return { ...merged, ...serverProps };
      }
    } catch {
      /* garde les métadonnées communauté sans QR */
    }
    return merged;
  }

  if (comp.type === 'hubupNotifications') {
    const communities = await fetchHubupCommunitiesForPicker();
    const community = communities.find((c) => c.id === comp.props.groupId);
    const snapshot = await fetchNotificationsSnapshot(
      comp.props.groupId,
      comp.props.topicId || '',
      comp.props.notificationCount || 3,
      community,
    );
    return {
      ...comp.props,
      ...(community ? communityPickerPatch(community) : {}),
      ...snapshot,
    };
  }

  return comp.props;
}

export async function refreshHubupSnapshotsInSite(site) {
  if (!site?.sections?.length) return site;

  const sections = await Promise.all(
    site.sections.map(async (sec) => {
      const components = await Promise.all(
        (sec.components || []).map(async (comp) => {
          if (comp.type !== 'hubupQr' && comp.type !== 'hubupNotifications') return comp;
          if (!comp.props?.groupId) return comp;
          const props = await refreshHubupComponentProps(comp);
          return { ...comp, props };
        }),
      );
      return { ...sec, components };
    }),
  );

  return { ...site, sections };
}

export function buildInviteUrlFromProps(props) {
  if (props?.inviteUrl) return props.inviteUrl;
  return buildHubupGroupInviteUrl(props?.inviteCode);
}
